import { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { ClientSummary } from '../../../2_domain/client/client';
import { Staff } from '../../../2_domain/staff/staff';
import { TestItemSummary } from '../../../2_domain/test/test';
import Column from '../../core/components/box/column';
import SizedBox from '../../core/components/box/sizedBox';
import LXLargeButton from '../../core/components/buttons/lxLargeButton';
import { LXbody, LXbodyLong, LXCaption, LXh4 } from '../../core/texts/lxText';
import { ReactComponent as Close } from '../../../assets/icons/closeGray.svg';
import Row from '../../core/components/box/row';
import { toIDPrice } from '../../../lib/util/numberFormatter';

interface TestAddSideBarProps {
  client?: ClientSummary;
  testItems: TestItemSummary[];
  receptionist?: Staff;
  attendingPhysician?: Staff;
  isFirstStep: boolean;
  removeTestItem: (testItem: TestItemSummary) => void;
  onPrev: () => void;
  onNext: () => void;
  nextDisabled: boolean;
}

const ScrollArea = styled.div`
  overflow-y: overlay;
  max-height: calc(100vh - 60px - 70px - 174px);
`;

const SiderWrapper = styled.div`
  width: 240px;
`;

interface SiderContainerProps {
  position?: string;
}

const SiderContainer = styled.div<SiderContainerProps>`
  border-radius: 12px;
  padding: 30px 0px;
  width: 240px;
  position: ${(props) => props.position};
  top: ${(props) => (props.position === 'fixed' ? '30px' : `default`)};
  max-height: calc(100vh - 60px);
  background-color: ${(props) => props.theme.colors.white};
`;

const Divider = styled.div`
  height: 1px;
  margin: 20px 0px;
  background-color: ${(props) => props.theme.colors.grayLine};
`;

const Padding = styled.div`
  padding: 0px 30px;
  width: 100%;
`;

const TestAddSideBar = (props: TestAddSideBarProps) => {
  const theme = useTheme();
  const [position, setPosition] = useState<string>();
  const ref = useRef<any>();

  const buildClient = () => {
    if (props.client) {
      return (
        <Column>
          <LXbody>{props.client.name}</LXbody>
          <SizedBox height={4}></SizedBox>
          <LXCaption>{`#${props.client.clientSerial}`}</LXCaption>
        </Column>
      );
    } else {
      return <LXbody>-</LXbody>;
    }
  };

  const buildItemCard = () => {
    return props.testItems.map((item, idx) => {
      if (idx === 0) {
        return (
          <TestItemCard
            key={item.id}
            item={item}
            removeItem={props.removeTestItem}
          ></TestItemCard>
        );
      } else {
        return (
          <Column key={item.id}>
            <SizedBox height={8}></SizedBox>
            <TestItemCard
              item={item}
              removeItem={props.removeTestItem}
            ></TestItemCard>
          </Column>
        );
      }
    });
  };

  const buildTestItems = () => {
    if (props.testItems.length !== 0) {
      return buildItemCard();
    } else {
      return <LXbody>-</LXbody>;
    }
  };

  const buildTotalPrice = () => {
    if (props.testItems.length !== 0) {
      return (
        <LXbody>
          {toIDPrice(
            props.testItems.reduce((acc, cur) => acc + cur.amount, 0),
            true
          )}
        </LXbody>
      );
    } else {
      return <LXbody>-</LXbody>;
    }
  };

  const buildManager = (staff: Staff | undefined) => {
    if (staff) {
      return (
        <LXbodyLong lineHeight="18px">{`${staff.name} (${staff.phone})`}</LXbodyLong>
      );
    } else {
      return <LXbody>-</LXbody>;
    }
  };

  const scrollListener = () => {
    const scrollMax = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.clientHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    );

    const siderHeight = ref.current.clientHeight;
    if (window.scrollY > 175 && scrollMax > siderHeight + 330) {
      setPosition('fixed');
    } else {
      setPosition(undefined);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', scrollListener);
    return () => {
      window.removeEventListener('scroll', scrollListener);
    };
  }, []);

  return (
    <SiderWrapper ref={ref}>
      <SiderContainer position={position}>
        <Column height="100%">
          <Padding>
            <LXh4>{theme.texts.testInformation}</LXh4>
            <SizedBox height={20}></SizedBox>
          </Padding>
          <ScrollArea>
            <Padding>
              <SizedBox height={10} />
              <LXCaption>{theme.texts.client}</LXCaption>
              <SizedBox height={10}></SizedBox>
              {buildClient()}
              <Divider></Divider>
              <LXCaption>{theme.texts.testItem}</LXCaption>
              <SizedBox height={10}></SizedBox>
              {buildTestItems()}
              <Divider></Divider>
              <LXCaption>{theme.texts.totalPrice}</LXCaption>
              <SizedBox height={10}></SizedBox>
              {buildTotalPrice()}
              <Divider></Divider>
              <LXCaption>{theme.staff.receptionist}</LXCaption>
              <SizedBox height={10}></SizedBox>
              {buildManager(props.receptionist)}
              <Divider></Divider>
              <LXCaption>{theme.staff.attendingPhysician}</LXCaption>
              <SizedBox height={10}></SizedBox>
              {buildManager(props.attendingPhysician)}
            </Padding>
          </ScrollArea>
          <SizedBox height={40}></SizedBox>
          <Padding>
            <LXLargeButton
              width={'100%'}
              disabled={props.nextDisabled}
              onClick={async () => props.onNext()}
            >
              {theme.texts.next}
            </LXLargeButton>
            {props.isFirstStep ? null : <SizedBox height={8}></SizedBox>}
            {props.isFirstStep ? null : (
              <LXLargeButton
                width={'100%'}
                onClick={async () => props.onPrev()}
                kind={'secondary'}
              >
                {theme.texts.prev}
              </LXLargeButton>
            )}
          </Padding>
        </Column>
      </SiderContainer>
    </SiderWrapper>
  );
};

interface TestItemCardProps {
  item: TestItemSummary;
  removeItem: (item: TestItemSummary) => void;
}

const TestItemCardContainer = styled.div`
  width: 180px;
  padding: 10px 12px;
  border-radius: 12px;
  background-color: ${(props) => props.theme.colors.grayBgLight};
`;

const TestItemCard = (props: TestItemCardProps) => {
  return (
    <TestItemCardContainer>
      <Column>
        <Row justifyContent="space-between">
          <LXbodyLong lineHeight="18px">{props.item.name}</LXbodyLong>
          <Close
            cursor={'pointer'}
            onClick={() => props.removeItem(props.item)}
          ></Close>
        </Row>
        <SizedBox height={4}></SizedBox>
        <LXCaption>{`#${props.item.serial}`}</LXCaption>
      </Column>
    </TestItemCardContainer>
  );
};

export default TestAddSideBar;
